import { Component, ElementRef } from '@angular/core';

import { ComponentsService } from '../../services/components.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { BrandingService } from '../services/branding.service';
import { UserComponentsService } from '../services/user-components.service';
import { LogoImageService } from '../services/logo-image.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { FileMetadataUtilsService } from '../services/file-metadata-utils.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { environment } from 'src/environments/environment';

interface BrandColors {
  baseColor?: string | null;
  accentColor?: string | null;
}

@Component({
  selector: 'template-component-branding',
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss'],
  standalone: false,
})
export class BrandingComponent {
  public componentId: string;
  public defaultType: string;
  public type: string;
  public timezoneType: string;
  public timezone: string;
  public timeFormat: string;
  public dateFormat: string;

  brandingAvailable = this.featuresService.isFeatureAvailable(
    FeaturesService.FEATURE_BRANDING,
  );
  brandColorsExpanded = false;
  isLogoUploading = false;
  logoUploadList: File[] = [];
  private lastCustomColor: BrandColors = { baseColor: null, accentColor: null };
  logoUploadManager = {
    onUploadStatus: (isUploading: boolean) => {
      this.isLogoUploading = isUploading;
    },
    addFile: (file: File) => {
      this.updateLogoMetadata([file]);
    },
    isSingleFileSelector: () => true,
  };

  constructor(
    private elementRef: ElementRef,
    private componentsFactory: ComponentsService,
    private attributeDataFactory: AttributeDataService,
    private featuresService: FeaturesService,
    private userComponentsService: UserComponentsService,
    private logoImageService: LogoImageService,
    private storageManagerService: StorageManagerService,
    private fileMetadataUtilsService: FileMetadataUtilsService,
    private broadcaster: BroadcasterService,
    public brandingFactory: BrandingService,
  ) {
    this.componentsFactory.registerDirective({
      type: 'rise-branding',
      element: this.elementRef.nativeElement,
      show: () => {
        this.loadLastCustomColor();
      },
    });
  }

  get canEditCompanyBranding() {
    return this.brandingAvailable && this.brandingFactory.canEditBranding();
  }

  get canOverrideBranding() {
    return !!this.brandingAvailable;
  }

  editLogo() {
    if (this.canEditCompanyBranding) {
      this.componentsFactory.editComponent({
        type: 'rise-image-logo',
      });
    }
  }

  addLogoToPresentation() {
    if (this.brandingAvailable) {
      const metadata = this.logoImageService.getImagesAsMetadata();
      const logo = metadata[0];

      if (!logo?.file) {
        return;
      }

      this.userComponentsService.addUserComponentsMediaItems(
        [
          {
            attributes: {
              editorLabel: 'Logo',
              environment: environment.production ? 'prod' : 'test',
              isLogo: true,
              source: logo.file,
              type: 'storage-file',
            },
          },
        ],
        false,
        'image',
      );
    }
  }

  activateLogoUploader(event?: Event) {
    event?.stopPropagation();

    if (!this.canEditCompanyBranding || this.isLogoUploading) {
      return;
    }

    const uploader = document.getElementById(
      'brand-logo-uploader',
    ) as HTMLInputElement;
    uploader?.click();
  }

  selectLogoFromStorage(event?: Event) {
    event?.stopPropagation();

    if (!this.canEditCompanyBranding || this.isLogoUploading) {
      return;
    }

    this.storageManagerService.fileType = StorageManagerService.FILE_TYPE.IMAGE;
    this.storageManagerService.isSingleFileSelector = () => true;
    this.storageManagerService.onSelectHandler = (newSelectedItems) => {
      this.updateLogoMetadata(newSelectedItems, true);
    };

    this.componentsFactory.editComponent({
      type: 'rise-storage-selector',
    });
  }

  removeLogo(event?: Event) {
    event?.stopPropagation();

    const logoMetadata = this.logoImageService.getImagesAsMetadata();

    if (!this.canEditCompanyBranding || !logoMetadata.length) {
      return;
    }

    return this.logoImageService
      .removeImage(logoMetadata[0], logoMetadata)
      .then((metadata: any[]) => {
        if (!metadata?.length) {
          return this.brandingFactory.saveBranding(true);
        }
      });
  }

  editColors() {
    if (this.canEditCompanyBranding) {
      this.enableBrandColors();
      this.brandColorsExpanded = true;
    }
  }

  get brandingSettings() {
    if (!this.brandingFactory.brandingSettings) {
      this.brandingFactory.brandingSettings = {};
    }

    return this.brandingFactory.brandingSettings;
  }

  get logoMetadata() {
    return this.logoImageService.getImagesAsMetadata();
  }

  get hasLogoSet() {
    return this.logoMetadata.some((logo) => !!logo?.file);
  }

  get logoThumbnailUrl() {
    return this.logoMetadata[0]?.['thumbnail-url'];
  }

  get customColors(): BrandColors {
    return (
      this.attributeDataFactory.getAttributeDataGlobal('brandingOverride') ?? {}
    );
  }

  isBrandColorsEnabled() {
    return !this.isCustomColorsEnabled();
  }

  isCustomColorsEnabled() {
    return !!this.attributeDataFactory.getAttributeDataGlobal(
      'brandingOverride',
    );
  }

  toggleBrandColors(enabled: boolean) {
    if (!this.canOverrideBranding) {
      return;
    }

    if (enabled) {
      this.enableBrandColors();
      this.brandColorsExpanded = this.canEditCompanyBranding;
    } else {
      this.enableCustomColors();
    }
  }

  toggleCustomColors(enabled: boolean) {
    if (!this.canOverrideBranding) {
      return;
    }

    if (enabled) {
      this.enableCustomColors();
    } else {
      this.enableBrandColors();
    }
  }

  saveBranding() {
    if (!this.canEditCompanyBranding) {
      return;
    }

    this.brandingFactory.setUnsavedChanges();
  }

  saveBrandColor(colorKey: 'baseColor' | 'accentColor', color: string) {
    if (!this.canEditCompanyBranding) {
      return;
    }

    this.brandingSettings[colorKey] = color;
    this.saveBranding();
  }

  saveCustomColor(colorKey: 'baseColor' | 'accentColor', color: string) {
    if (!this.canOverrideBranding) {
      return;
    }

    const customColors: BrandColors = {
      ...this.customColors,
      [colorKey]: color,
    };

    this.lastCustomColor = {
      baseColor: customColors.baseColor ?? null,
      accentColor: customColors.accentColor ?? null,
    };
    this.attributeDataFactory.setAttributeDataGlobal('brandingOverride', {
      ...this.lastCustomColor,
    });
    this.emitPresentationUnsavedChanges();
  }

  closeBrandColorsEditor() {
    this.brandColorsExpanded = false;
  }

  showUpgradePlanModal() {
    this.featuresService.showUpgradePlanModal(FeaturesService.FEATURE_BRANDING);
  }

  private enableBrandColors() {
    this.storeLastCustomColor();

    this.attributeDataFactory.setAttributeDataGlobal('brandingOverride', null);
    this.emitPresentationUnsavedChanges();
  }

  private enableCustomColors() {
    this.brandColorsExpanded = false;

    this.attributeDataFactory.setAttributeDataGlobal('brandingOverride', {
      baseColor: this.lastCustomColor.baseColor ?? null,
      accentColor: this.lastCustomColor.accentColor ?? null,
    });
    this.emitPresentationUnsavedChanges();
  }

  private storeLastCustomColor() {
    const customColors = this.customColors;

    if (!customColors.baseColor && !customColors.accentColor) {
      return;
    }

    this.lastCustomColor = {
      baseColor: customColors.baseColor ?? null,
      accentColor: customColors.accentColor ?? null,
    };
  }

  // Seeds the in memory colors with the colors already applied to the
  // presentation, so reopening the panel keeps showing them on the pickers.
  // Never clears them: colors picked earlier in this session must survive
  // navigating away while brand colors are enabled.
  private loadLastCustomColor() {
    this.storeLastCustomColor();
  }

  private emitPresentationUnsavedChanges() {
    this.broadcaster.emit(
      'risevision.template-editor.presentationUnsavedChanges',
    );
  }

  private updateLogoMetadata(files: any[], alwaysAppend = false) {
    const metadata = this.fileMetadataUtilsService.metadataWithFile(
      [],
      LogoImageService.DEFAULT_IMAGE_THUMBNAIL,
      files,
      alwaysAppend,
    );

    this.logoImageService.updateMetadata(metadata);
    if (metadata?.length) {
      this.brandingFactory.saveBranding(true);
    }
  }
}
